import qs from 'qs';
import type { Entrypoint, Surface } from 'types';

const STORAGE_KEY = 'pageContext';

const PLP_SEGMENTS = ['release-dates', 'sole-market', 'clothing'];

const SEARCH_ENGINES = [
  'baidu',
  'bing',
  'duckduckgo',
  'ecosia',
  'google',
  'yahoo',
  'yandex'
];

const SOCIAL_MEDIUMS = ['social', 'social-paid', 'social-organic'];

const SEARCH_MEDIUMS = ['cpc', 'organic', 'paid-search', 'ppc'];

const SOCIAL_SOURCES = [
  'facebook',
  'instagram',
  'linkedin',
  'pinterest',
  'reddit',
  'snapchat',
  'tiktok',
  'twitter',
  'youtube'
];

type StoredState = {
  currentContext: unknown;
  entrypoint: Entrypoint;
  previousContext: unknown;
  previousPagePath: string | null;
  referrer: string;
  surfaceSource: Surface;
  url: string | null;
};

const DEFAULT_STATE: StoredState = {
  currentContext: null,
  entrypoint: 'unknown',
  previousContext: null,
  previousPagePath: null,
  referrer: '',
  surfaceSource: 'direct',
  url: null
};

function load(): StoredState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    // sessionStorage may be unavailable
  }

  return { ...DEFAULT_STATE };
}

function save(state: StoredState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable
  }
}

function deriveEntrypoint(
  utmSource?: string,
  utmMedium?: string,
  documentReferrer?: string
): Entrypoint {
  const source = utmSource?.toLowerCase() ?? '';
  const medium = utmMedium?.toLowerCase() ?? '';

  if (medium === 'email') return 'email';

  if (SOCIAL_SOURCES.includes(source) || SOCIAL_MEDIUMS.includes(medium)) {
    return 'social';
  }

  if (SEARCH_MEDIUMS.includes(medium)) return 'search';

  try {
    const hostname = new URL(documentReferrer ?? '').hostname.toLowerCase();
    if (SEARCH_ENGINES.some((engine) => hostname.includes(engine))) {
      return 'search';
    }
  } catch {
    // Invalid or empty referrer URL
  }

  return 'unknown';
}

function deriveEntrypointFromLandingParams(state: StoredState): Entrypoint {
  const isFirstNavigation = state.url === null;

  if (!isFirstNavigation) return state.entrypoint;

  const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const utmSource =
    typeof params.utm_source === 'string' ? params.utm_source : undefined;
  const utmMedium =
    typeof params.utm_medium === 'string' ? params.utm_medium : undefined;

  return deriveEntrypoint(utmSource, utmMedium, document.referrer);
}

function deriveReferrer(state: StoredState): string {
  if (state.url === null) return document.referrer || 'unknown';

  return window.location.origin + state.url;
}

function deriveSurfaceFromUrl(url: string | null): Surface {
  if (url === null) return 'direct';

  const segments = url.split('?')[0]!.split('/').filter(Boolean);

  if (segments.length === 0) return 'homepage';
  if (segments[0] === 'news' || segments[0] === 'features') return 'article';
  if (segments[0] === 'sale') return 'sale';
  if (segments[0] === 's') return 'pdp';

  const plpIndex = segments.findIndex((s) => PLP_SEGMENTS.includes(s));

  if (plpIndex !== -1) {
    const segmentsAfterKey = segments.length - plpIndex - 1;

    return segmentsAfterKey >= 3 ? 'pdp' : 'plp';
  }

  return 'direct';
}

export function setNavigationContext<T = unknown>(context: T) {
  const state = load();

  state.currentContext = context;
  save(state);
}

export function getCurrentContext<T = unknown>(): T | null {
  return (load().currentContext as T) ?? null;
}

export function getPreviousContext<P = unknown>(): P | null {
  return (load().previousContext as P) ?? null;
}

export function getEntrypoint(): Entrypoint {
  return load().entrypoint;
}

export function getPreviousPagePath(): string | null {
  return load().previousPagePath;
}

export function getReferrer(): string {
  return load().referrer;
}

export function getSurfaceSource(): Surface {
  const state = load();
  const ctx = state.previousContext as { surfaceSource?: Surface } | null;

  return ctx?.surfaceSource ?? state.surfaceSource;
}

export function advanceNavigationContext(url: string) {
  if (typeof document === 'undefined') return;

  const state = load();

  if (url === state.url) return;

  const previousSurface = deriveSurfaceFromUrl(state.url);

  save({
    ...state,
    currentContext: null,
    entrypoint: deriveEntrypointFromLandingParams(state),
    previousContext: state.currentContext,
    previousPagePath: state.url?.split(/[?#]/)[0] ?? null,
    referrer: deriveReferrer(state),
    surfaceSource: previousSurface,
    url
  });
}
